import { useRouter } from 'next/router'

import { useSatuEventTracker } from 'utils/satuEvent'
import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'
import { APP_DEVICE_ID_STORAGE_KEY } from 'configs/android'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

import type { TSETrackProfileActivities } from 'types/profile'

type TSETrackEventDefaultAtributes = {
  screen_name: string
  is_login: [boolean, 'bool']
  is_webview: [boolean, 'bool']
  email: string
  profile_user_id: string
}

const SATU_EVENT_NAME = 'guru_core_pwa_event_tracker_v1'
const TRACKER_EVENT_NAME = 'GURU_CORE_APP'
const TRACKER_SCREEN_NAME = 'PROFILE_PAGE'
const DEFAULT_TIMEOUT = 5000 // 5 seconds

export const useSETrackEventToolkits = () => {
  const [, , , getAppDeviceIdValue] = useLocalStorage(
    APP_DEVICE_ID_STORAGE_KEY,
    null
  )
  const { query } = useRouter()
  const { id: profileId } = query

  const appDeviceID = getAppDeviceIdValue()
  const hasAppDeviceID = Boolean(appDeviceID)
  const androidHandler = AndroidHandler()
  const { session } = useAuth()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const SETrackEventDefaultAtributes: TSETrackEventDefaultAtributes = {
    screen_name: TRACKER_SCREEN_NAME,
    is_login: [Boolean(session), 'bool'],
    is_webview: [androidHandler.enabled, 'bool'],
    email: session?.user?.email ?? null,
    profile_user_id: profileId as string,
  }

  const SETrackComponent = (
    activity_type: TSETrackProfileActivities,
    additionalAttributes?: Record<string, any>
  ): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        activity_type,
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: TRACKER_SCREEN_NAME,
          ...additionalAttributes,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  type TSETrackClickToolkitCategoryProps = {
    isNarasumber: boolean
    toolkitType: string
    numToolkit: number
    numTypeToolkit: number
  }
  const SETrackClickToolkitCategory = ({
    isNarasumber,
    toolkitType,
    numToolkit,
    numTypeToolkit,
  }: TSETrackClickToolkitCategoryProps): void => {
    SETrackComponent('CLICK_TOOLKIT_PROFILE_CATEGORY', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      num_toolkit: [numToolkit, 'int'],
      toolkit_type: toolkitType,
      num_type_toolkit: [numTypeToolkit, 'int'],
    })
  }

  type TSETrackClickToolkitItemProps = {
    toolkitType: string
    toolkitLocation: number
    toolkitId: string
    numTypeToolkit: number
  }
  const SETrackClickToolkitItem = ({
    toolkitType,
    toolkitLocation,
    toolkitId,
    numTypeToolkit,
  }: TSETrackClickToolkitItemProps): void => {
    SETrackComponent('CLICK_TOOLKIT_PROFILE_DETAIL', {
      event_trigger: 'TOOLKIT_PROFILE_PAGE',
      screen_name: 'TOOLKIT_PROFILE_PAGE',
      toolkit_type: toolkitType,
      num_type_toolkit: [numTypeToolkit, 'int'],
      toolkit_location: [toolkitLocation, 'int'],
      toolkit_id: toolkitId,
    })
  }

  return {
    SETrackClickToolkitCategory,
    SETrackClickToolkitItem,
  }
}
